import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  SimpleGrid,
  useColorModeValue,
  Heading,
  Flex,
  Icon,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Button,
  InputGroup,
  InputLeftElement,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  IconButton,
  Spacer,
  Textarea,
  Switch,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Divider,
  Progress
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Building2, Bot, Mic, Users, Activity, TrendingUp, MessageSquare, Clock, Shield, Settings, UserCheck } from 'lucide-react';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiPlusCircle, FiMinusCircle, FiMessageSquare, FiDollarSign } from 'react-icons/fi';
import PageLayout from '../components/PageLayout';
import UserDashboardModal from './UserDashboardModal';
import { 
  getClubs, 
  getAdminStats, 
  getClubAnalytics, 
  updateClubTokens, 
  sendClubNotification 
} from '../services/api';

const statsData = [
  { name: "Active users", value: 24, icon: Building2, change: "+12%", color: "blue.500" },
  { name: "Chatbots Deployed", value: 156, icon: Bot, change: "+8%", color: "green.500" },
  { name: "Voice Agents", value: 89, icon: Mic, change: "+15%", color: "purple.500" },
  { name: "Total users Accounts", value: 3420, icon: Users, change: "+23%", color: "orange.500" },
];

const chartData = [
  { month: "Jan", conversations: 2400, agents: 85 },
  { month: "Feb", conversations: 2800, agents: 92 },
  { month: "Mar", conversations: 3200, agents: 98 },
  { month: "Apr", conversations: 3600, agents: 110 },
  { month: "May", conversations: 4200, agents: 125 },
  { month: "Jun", conversations: 4800, agents: 135 },
];

const usageData = [
  { name: "Chatbots", value: 65, color: "#3B82F6" },
  { name: "Voice Agents", value: 25, color: "#10B981" },
  { name: "Integrations", value: 10, color: "#F59E0B" },
];

const dummyStats = {
  totalClubAccounts: 3420,
  totalTokens: 1000000,
  activeClubs: 24,
  inactiveClubs: 4,
};

const dummyClubs = [
  { 
    id: 1, 
    name: 'Tech user Alpha', 
    email: 'admin@techuser.com', 
    status: 'active', 
    totalTokens: 50000,
    lastActive: '2 minutes ago' 
  },
  { 
    id: 2, 
    name: 'Sports user Beta', 
    email: 'admin@sportsuser.com', 
    status: 'active', 
    totalTokens: 75000,
    lastActive: '1 hour ago' 
  },
];

const AdminDashboard = () => {
  const toast = useToast();
  
  const { isOpen: isTokenModalOpen, onOpen: onTokenModalOpen, onClose: onTokenModalClose } = useDisclosure();
  const { isOpen: isNotificationModalOpen, onOpen: onNotificationModalOpen, onClose: onNotificationModalClose } = useDisclosure();
  const { isOpen: isClubModalOpen, onOpen: onClubModalOpen, onClose: onClubModalClose } = useDisclosure();

  const [stats, setStats] = useState({
    totalClubAccounts: 0,
    totalUsers: 0,
    activeClubs: 0,
    inactiveClubs: 0,
    totalTokens: 0,
    userStats: {},
    recentClubs: [],
    recentUsers: [],
    clubAnalytics: []
  });
  const [selectedClub, setSelectedClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenAmount, setTokenAmount] = useState(0);
  const [isAddingTokens, setIsAddingTokens] = useState(true);
  const [notificationRecipient, setNotificationRecipient] = useState('all');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendAsEmail, setSendAsEmail] = useState(false);

  const [clubs, setClubs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClubAccount, setSelectedClubAccount] = useState(null);
  const [selectedClubAccountForModal, setSelectedClubAccountForModal] = useState(null);
  const [newClubAccount, setNewClubAccount] = useState({
    name: '',
    email: '',
    totalTokens: 1000,
    status: 'active',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const statCardBg = useColorModeValue('gray.100', 'gray.900');
  const fieldBg = useColorModeValue('gray.100', 'gray.700');
  const fieldBorder = useColorModeValue('gray.200', 'gray.600');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBorder = useColorModeValue('blue.300', 'blue.600');
  const focusBorder = useColorModeValue('blue.500', 'blue.500');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, clubsData] = await Promise.all([
        getAdminStats(),
        getClubs()
      ]);
      const clubsWithAnalytics = await Promise.all(
        clubsData.data.map(async (club) => {
          try {
            const analytics = await getClubAnalytics(club._id);
            return { ...club, analytics };
          } catch (err) {
            console.error(`Error fetching analytics for user ${club._id}:`, err);
            return club;
          }
        })
      );
      const totalUsers = clubsWithAnalytics.reduce((acc, club) => acc + (club.users ? club.users.length : 0), 0);
      setStats({ ...statsData, totalUsers });
      setClubs(clubsWithAnalytics);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClubAccount(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateClubAccount = (e) => {
    e.preventDefault();
    const newClubAccountWithId = {
      ...newClubAccount,
      id: clubs.length + 1,
      lastActive: 'Just now'
    };
    setClubs([...clubs, newClubAccountWithId]);
    toast({
      title: 'User Account Created',
      description: 'New user account has been created successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onTokenModalClose();
    setNewClubAccount({
      name: '',
      email: '',
      totalTokens: 1000,
      status: 'active',
    });
  };

  const handleEditClubAccount = (account) => {
    setSelectedClubAccount(account);
  };

  const handleUpdateClubAccount = (e) => {
    e.preventDefault();
    setClubs(clubs.map(account => 
      account.id === selectedClubAccount.id ? selectedClubAccount : account
    ));
    toast({
      title: 'user Account Updated',
      description: 'user account information has been updated successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    setSelectedClubAccount(null);
  };

  const handleDeleteClubAccount = (accountId) => {
    setClubs(clubs.filter(account => account.id !== accountId));
    toast({
      title: 'user Account Deleted',
      description: 'user account has been deleted successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleTokenChange = useCallback(async () => {
    if (!selectedClub) return;

    try {
      await updateClubTokens(selectedClub.id, tokenAmount, isAddingTokens);
      await fetchData();
      
      toast({
        title: isAddingTokens ? 'Tokens Added' : 'Tokens Deducted',
        description: `${tokenAmount} tokens have been ${isAddingTokens ? 'added to' : 'deducted from'} the user account`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onTokenModalClose();
      setTokenAmount(0);
    } catch (error) {
      console.error('Error updating tokens:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update tokens',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedClub, tokenAmount, isAddingTokens, fetchData, onTokenModalClose, toast]);

  const handleClubAccountClick = async (account) => {
    try {
      const analytics = await getClubAnalytics(account._id);
      setSelectedClubAccountForModal({
        username: account.name,
        tokens: {
          total: account.totalTokens,
          remaining: analytics.remainingTokens,
          increase: analytics.tokenIncrease,
        },
        agents: {
          chat: {
            total: analytics.chatAgents.total,
            active: analytics.chatAgents.active,
            increase: analytics.chatAgents.increase,
            successRate: analytics.chatAgents.successRate,
            avgResponseTime: analytics.chatAgents.avgResponseTime,
          },
          voice: {
            total: analytics.voiceAgents.total,
            active: analytics.voiceAgents.active,
            increase: analytics.voiceAgents.increase,
            totalDuration: analytics.voiceAgents.totalDuration,
            qualityScore: analytics.voiceAgents.qualityScore,
          },
        },
        billing: {
          plan: account.status === 'active' ? 'Premium' : 'Basic',
          nextBillingDate: analytics.nextBillingDate,
          monthlyVoiceMinutes: analytics.monthlyVoiceMinutes,
          monthlyChatTokens: analytics.monthlyChatTokens,
          estimatedCost: analytics.estimatedCost,
        },
      });
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user analytics',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSendNotification = useCallback(async (e) => {
    e.preventDefault();
    try {
      await sendClubNotification({
        recipient: notificationRecipient,
        message: notificationMessage,
        sendAsEmail
      });

      toast({
        title: 'Notification Sent',
        description: 'Notification has been sent successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onNotificationModalClose();
      setNotificationRecipient('all');
      setNotificationMessage('');
      setSendAsEmail(false);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to send notification',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [notificationRecipient, notificationMessage, sendAsEmail, onNotificationModalClose, toast]);

  const handleClubClick = useCallback(async (club) => {
    try {
      const analytics = await getClubAnalytics(club._id);
      setSelectedClub({ ...club, analytics });
      onClubModalOpen();
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user analytics',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [onClubModalOpen, toast]);

  const filteredClubs = clubs.filter(club => {
    const matchesSearch = 
      club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      club.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || club.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <PageLayout title="Admin Dashboard">
      <VStack spacing={6} align="stretch">
        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            <Flex alignItems="center">
              <Box>
                <Heading size="xl" color={headingColor}>Admin Dashboard</Heading>
                <Text color={textColor} mt={1}>Manage user accounts and token distribution</Text>
              </Box>
              <Spacer />
              <HStack spacing={4}>
                <Button leftIcon={<FiMessageSquare />} colorScheme="purple" size="md" onClick={onNotificationModalOpen}>
                  Send Notification
                </Button>
              </HStack>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mt={4}>
              <Card bg={statCardBg} boxShadow="md">
                <CardBody p={6}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>Total User Accounts</Text>
                      <Text fontSize="3xl" fontWeight="bold" color={headingColor}>{stats.totalClubAccounts}</Text>
                      <Text fontSize="sm" color={textColor}>
                        {stats.activeClubs} Active / {stats.inactiveClubs} Inactive
                      </Text>
                    </Box>
                    <Icon as={Users} boxSize={8} color="blue.500" />
                  </Flex>
                </CardBody>
              </Card>
              <Card bg={statCardBg} boxShadow="md">
                <CardBody p={6}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>Total Users</Text>
                      <Text fontSize="3xl" fontWeight="bold" color={headingColor}>{stats.totalUsers}</Text>
                    </Box>
                    <Icon as={Users} boxSize={8} color="green.500" />
                  </Flex>
                </CardBody>
              </Card>
              <Card bg={statCardBg} boxShadow="md">
                <CardBody p={6}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>Active user</Text>
                      <Text fontSize="3xl" fontWeight="bold" color={headingColor}>{stats.activeClubs}</Text>
                    </Box>
                    <Icon as={UserCheck} boxSize={8} color="purple.500" />
                  </Flex>
                </CardBody>
              </Card>
              <Card bg={statCardBg} boxShadow="md">
                <CardBody p={6}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>Inactive user</Text>
                      <Text fontSize="3xl" fontWeight="bold" color="red.500">{stats.inactiveClubs}</Text>
                    </Box>
                    <Icon as={Settings} boxSize={8} color="red.500" />
                  </Flex>
                </CardBody>
              </Card>
            </SimpleGrid>

            <Card bg={cardBg} boxShadow="lg" borderRadius="lg">
              <CardHeader>
                <Flex justifyContent="space-between" alignItems="center">
                  <Heading size="md" color={headingColor}>user Account List</Heading>
                  <HStack spacing={2}>
                    <InputGroup size="sm" width="200px">
                      <InputLeftElement
                        pointerEvents="none"
                        children={<FiSearch color={textColor} />}
                      />
                      <Input
                        type="text"
                        placeholder="Search accounts"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        borderRadius="md"
                        bg={fieldBg}
                        borderColor={fieldBorder}
                        _hover={{ borderColor: hoverBorder }}
                        _focus={{ borderColor: focusBorder, boxShadow: 'outline' }}
                      />
                    </InputGroup>
                    <Select
                      placeholder="Filter by status"
                      size="sm"
                      width="150px"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      borderColor={fieldBorder}
                      _hover={{ borderColor: hoverBorder }}
                      _focus={{ borderColor: focusBorder, boxShadow: 'outline' }}
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Select>
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th color={textColor}>user Account</Th>
                        <Th color={textColor}>Status</Th>
                        <Th color={textColor}>Total Tokens</Th>
                        <Th color={textColor}>Last Active</Th>
                        <Th color={textColor}>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredClubs.length === 0 ? (
                        <Tr>
                          <Td colSpan={6} textAlign="center" color={textColor}>
                            No club accounts found.
                          </Td>
                        </Tr>
                      ) : (
                        filteredClubs.map(account => (
                          <Tr 
                            key={account.id} 
                            _hover={{ bg: rowHoverBg }}
                            cursor="pointer"
                            onClick={() => handleClubClick(account)}
                          >
                            <Td py={4} px={4}>
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="bold" color={headingColor}>{account.name}</Text>
                                <Text fontSize="sm" color={textColor}>{account.email}</Text>
                              </VStack>
                            </Td>
                            <Td py={4} px={4}>
                              <Badge colorScheme={account.status === 'active' ? 'green' : 'red'} fontSize="xs">
                                {account.status}
                              </Badge>
                            </Td>
                            <Td py={4} px={4} color={textColor}>{account.totalTokens.toLocaleString()}</Td>
                            <Td py={4} px={4} color={textColor}>{account.lastActive}</Td>
                            <Td py={4} px={4}>
                              <HStack spacing={2}>
                                <Tooltip label="Add Tokens" hasArrow>
                                  <IconButton
                                    aria-label="Add Tokens"
                                    icon={<FiPlusCircle />}
                                    size="sm"
                                    colorScheme="green"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedClubAccount(account);
                                      setIsAddingTokens(true);
                                      onTokenModalOpen();
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip label="Deduct Tokens" hasArrow>
                                  <IconButton
                                    aria-label="Deduct Tokens"
                                    icon={<FiMinusCircle />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedClubAccount(account);
                                      setIsAddingTokens(false);
                                      onTokenModalOpen();
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip label="Edit Account" hasArrow>
                                  <IconButton
                                    aria-label="Edit Account"
                                    icon={<FiEdit2 />}
                                    size="sm"
                                    colorScheme="blue"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditClubAccount(account);
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip label="Delete Account" hasArrow>
                                  <IconButton
                                    aria-label="Delete Account"
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClubAccount(account.id);
                                    }}
                                  />
                                </Tooltip>
                              </HStack>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>

            <Modal isOpen={isTokenModalOpen} onClose={onTokenModalClose} size="md">
              <ModalOverlay />
              <ModalContent bg={cardBg}>
                <ModalHeader color={headingColor}>{isAddingTokens ? 'Add Tokens' : 'Deduct Tokens'}</ModalHeader>
                <ModalCloseButton color={textColor} />
                <ModalBody pb={6}>
                  {selectedClubAccount && (
                    <VStack spacing={4}>
                      <Text color={textColor}>
                        {isAddingTokens ? 'Add tokens to' : 'Deduct tokens from'} {selectedClubAccount.name}
                      </Text>
                      <FormControl isRequired>
                        <FormLabel color={textColor}>Amount</FormLabel>
                        <NumberInput
                          min={0}
                          value={tokenAmount}
                          onChange={(value) => setTokenAmount(parseInt(value))}
                        >
                          <NumberInputField 
                              borderColor={fieldBorder}
                              bg={fieldBg}
                              color={textColor}
                            />
                          <NumberInputStepper>
                            <NumberIncrementStepper color={textColor} />
                            <NumberDecrementStepper color={textColor} />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <Button
                        colorScheme={isAddingTokens ? 'green' : 'red'}
                        width="full"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTokenChange();
                        }}
                      >
                        {isAddingTokens ? 'Add Tokens' : 'Deduct Tokens'}
                      </Button>
                    </VStack>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>

            <UserDashboardModal
              isOpen={isClubModalOpen}
              onClose={onClubModalClose}
              userData={selectedClub}
            />

            <Modal isOpen={isNotificationModalOpen} onClose={onNotificationModalClose} size="lg">
              <ModalOverlay />
              <ModalContent bg={cardBg}>
                <ModalHeader color={headingColor}>Send Notification</ModalHeader>
                <ModalCloseButton color={textColor} />
                <ModalBody pb={6}>
                  <VStack spacing={4} as="form" onSubmit={handleSendNotification}>
                    <FormControl id="recipient-select" isRequired>
                      <FormLabel color={textColor}>Recipient</FormLabel>
                      <Select
                        placeholder="Select recipient"
                        value={notificationRecipient}
                        onChange={(e) => setNotificationRecipient(e.target.value)}
                        borderColor={fieldBorder}
                        bg={fieldBg}
                        color={textColor}
                      >
                        <option value="all">All user Accounts</option>
                        {clubs.map(account => (
                          <option key={account.id} value={account.email}>
                            {account.name} ({account.email})
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl id="notification-message" isRequired>
                      <FormLabel color={textColor}>Message</FormLabel>
                      <Textarea
                        placeholder="Enter your message"
                        value={notificationMessage}
                        onChange={(e) => setNotificationMessage(e.target.value)}
                        borderColor={fieldBorder}
                        bg={fieldBg}
                        color={textColor}
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="send-as-email" mb="0" color={textColor}>
                        Send as Email
                      </FormLabel>
                      <Switch 
                        id="send-as-email" 
                        isChecked={sendAsEmail} 
                        onChange={(e) => setSendAsEmail(e.target.checked)}
                        colorScheme="blue"
                      />
                    </FormControl>

                    <Button type="submit" colorScheme="blue" width="full">
                      Send Notification
                    </Button>
                  </VStack>
                </ModalBody>
              </ModalContent>
            </Modal>
          </>
        )}
      </VStack>
    </PageLayout>
  );
};

export default AdminDashboard;
